// frontend/src/features/viewer/viewerSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { IRootState } from "../app/store"; // <-- adjust path
import type { ISharedViewCamera, IViewSnapshotState } from "@multiconsult-group/multiplacer-core";

export type IViewerActiveSearch = "ROOM" | "COMPONENT";
export type IViewPresetListCategory = "builtIn" | "project" | "personal";

export type IViewPresetReference =
  | { source: "builtIn"; key: string }
  | { source: "user"; id: string };

// Tabs in ClickedMcBimItemDetails
export type IViewerMcBimDetailsActiveTab = "identification" | "systems" | "psets";

// How to render details panel
export type IViewerMcBimDetailsLayoutMode = "TABS" | "TABLE";

export type IViewerSelectedSection = "sideA" | "sideB" | "sideC" | "sideD" | "isometric";
export type IViewerSectionCenterCoordinate = [number, number];

export interface IViewerFreeTextSearchState {
  room: string;
  component: string;
  activeSearch: IViewerActiveSearch;
}


export interface IViewerState {
  viewPresetListCategory: IViewPresetListCategory;
  selectedViewPreset: IViewPresetReference | null;
  viewSettingsPreset: IViewPresetReference | null;
  activeSharedView: { id: string; state: IViewSnapshotState } | null;
  currentCamera: { floorGlobalId: string; camera: Pick<ISharedViewCamera, "zoom" | "centerWorld"> } | null;
  restoredCamera: { floorGlobalId: string; camera: ISharedViewCamera } | null;
  freeTextSearch: IViewerFreeTextSearchState;

  // The MongoDB _id (string) of the clicked McBimItem (or null when nothing is selected)
  clickedMcBimItemId: string | null;
  clickedObjectRoom: { projectNo: number; itemId: string; roomNumber: string } | null;

  selectedSection: IViewerSelectedSection;
  sectionCenterCoordinate: IViewerSectionCenterCoordinate | null;

}

const initialState: IViewerState = {
  viewPresetListCategory: "builtIn",
  selectedViewPreset: null,
  viewSettingsPreset: null,
  activeSharedView: null,
  currentCamera: null,
  restoredCamera: null,
  freeTextSearch: {
    room: "",
    component: "",
    activeSearch: "ROOM",
  },

  clickedMcBimItemId: null,
  clickedObjectRoom: null,

  selectedSection: "sideA",
  sectionCenterCoordinate: null,

};

export const viewerSlice = createSlice({
  name: "viewer",
  initialState,
  reducers: {
    setViewPresetListCategoryInRedux(state, action: PayloadAction<IViewPresetListCategory>) {
      state.viewPresetListCategory = action.payload;
    },
    setSelectedViewPresetInRedux(state, action: PayloadAction<IViewPresetReference | null>) {
      state.selectedViewPreset = action.payload;
    },
    setViewSettingsPresetInRedux(state, action: PayloadAction<IViewPresetReference | null>) {
      state.viewSettingsPreset = action.payload;
    },
    setActiveSharedViewInRedux(state, action: PayloadAction<IViewerState["activeSharedView"]>) {
      state.activeSharedView = action.payload;
    },
    restoreViewerCameraInRedux(state, action: PayloadAction<NonNullable<IViewerState["restoredCamera"]>>) {
      state.restoredCamera = action.payload;
      state.currentCamera = action.payload;
    },
    setCurrentViewerCameraInRedux(state, action: PayloadAction<NonNullable<IViewerState["currentCamera"]>>) {
      const previous = state.currentCamera;
      const next = action.payload;
      if (previous?.floorGlobalId === next.floorGlobalId &&
        previous.camera.zoom === next.camera.zoom &&
        previous.camera.centerWorld.x === next.camera.centerWorld.x &&
        previous.camera.centerWorld.y === next.camera.centerWorld.y) return;
      state.currentCamera = next;
    },
    // -------------------- Search (rooms/components list) --------------------
    setActiveSearchInRedux(state, action: PayloadAction<IViewerActiveSearch>) {
      state.freeTextSearch.activeSearch = action.payload;
    },

    setRoomSearchTextInRedux(state, action: PayloadAction<string>) {
      state.freeTextSearch.room = action.payload;
    },

    setComponentSearchTextInRedux(state, action: PayloadAction<string>) {
      state.freeTextSearch.component = action.payload;
    },

    clearRoomSearchInRedux(state) {
      state.freeTextSearch.room = "";
    },

    clearComponentSearchInRedux(state) {
      state.freeTextSearch.component = "";
    },

    clearFreeTextSearchInRedux(state) {
      state.freeTextSearch.room = "";
      state.freeTextSearch.component = "";
      state.freeTextSearch.activeSearch = "ROOM";
    },

    // -------------------- Selection --------------------
    setClickedMcBimItemIdInRedux(state, action: PayloadAction<string | null>) {
      state.clickedMcBimItemId = action.payload;
      state.clickedObjectRoom = null;
    },
    setClickedObjectRoomInRedux(state, action: PayloadAction<IViewerState["clickedObjectRoom"]>) {
      if (!action.payload || action.payload.itemId === state.clickedMcBimItemId) {
        state.clickedObjectRoom = action.payload;
      }
    },
    clearClickedMcBimItemIdInRedux(state) {
      state.clickedMcBimItemId = null;
      state.clickedObjectRoom = null;
    },

    setSelectedSectionInRedux(state, action: PayloadAction<IViewerSelectedSection>) {
      state.selectedSection = action.payload;
    },

    setSectionCenterCoordinateInRedux(
      state,
      action: PayloadAction<IViewerSectionCenterCoordinate | null>
    ) {
      state.sectionCenterCoordinate = action.payload;
    },

  },
});

export const {
  setViewPresetListCategoryInRedux,
  setSelectedViewPresetInRedux,
  setViewSettingsPresetInRedux,
  setActiveSharedViewInRedux,
  setCurrentViewerCameraInRedux,
  restoreViewerCameraInRedux,
  setActiveSearchInRedux,
  setRoomSearchTextInRedux,
  setComponentSearchTextInRedux,
  clearRoomSearchInRedux,
  clearComponentSearchInRedux,
  clearFreeTextSearchInRedux,
  setClickedMcBimItemIdInRedux,
  clearClickedMcBimItemIdInRedux,
  setClickedObjectRoomInRedux,
  setSelectedSectionInRedux,
  setSectionCenterCoordinateInRedux,
} = viewerSlice.actions;

export const viewerReducer = viewerSlice.reducer;

// -------------------- Selectors --------------------

export const selectViewer = (state: IRootState): IViewerState => state.viewer;

export const selectViewerFreeTextSearch = (state: IRootState) => selectViewer(state).freeTextSearch;

export const selectRoomSearchText = (state: IRootState) => selectViewerFreeTextSearch(state).room;

export const selectComponentSearchText = (state: IRootState) =>
  selectViewerFreeTextSearch(state).component;

export const selectActiveSearch = (state: IRootState) =>
  selectViewerFreeTextSearch(state).activeSearch;

export const selectActiveSearchText = (state: IRootState) => {
  const s = selectViewerFreeTextSearch(state);
  return s.activeSearch === "ROOM" ? s.room : s.component;
};

export const selectClickedMcBimItemId = (state: IRootState) =>
  selectViewer(state).clickedMcBimItemId;

export const selectSelectedSection = (state: IRootState) =>
  selectViewer(state).selectedSection;

export const selectSectionCenterCoordinate = (state: IRootState) =>
  selectViewer(state).sectionCenterCoordinate;

export const selectClickedObjectRoomNumber = (state: IRootState) => {
  const room = state.viewer.clickedObjectRoom;
  return room?.projectNo === state.design.projectNo && room?.itemId === state.viewer.clickedMcBimItemId
    ? room?.roomNumber : undefined;
};
